import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { FileText, Trash2 } from '@/design-system/icons';
import { ConfirmModal } from '@/components';
import { getContractDocumentUrl } from '../api';
import { useContractDocuments, useDeleteContractDocument } from '../hooks';
import type { ServiceContractDocument } from '../types';

interface ContractDocumentListProps {
  deviceId: string;
  contractId: string;
}

// ContractDocumentList shows every document on a contract; rows open the document and the
// trash action soft-deletes it after a confirmation dialog.
export function ContractDocumentList({
  deviceId,
  contractId,
}: ContractDocumentListProps) {
  const { t } = useTranslation();
  const { data: documents, isLoading } = useContractDocuments(contractId);
  const deleteDocument = useDeleteContractDocument(deviceId, contractId);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

  // Resolve a signed URL for the clicked document and open it in a new tab.
  const handleOpen = async (documentId: string) => {
    try {
      const url = await getContractDocumentUrl(contractId, documentId);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      toast.error(t('serviceContracts.documents.openError'));
    }
  };

  const handleConfirmDelete = () => {
    if (!pendingDeleteId) return;
    deleteDocument.mutate(pendingDeleteId, {
      onSettled: () => setPendingDeleteId(null),
    });
  };

  if (isLoading) {
    return <p className="text-muted text-sm">{t('common.loading')}</p>;
  }

  if (!documents || documents.length === 0) {
    return (
      <p className="text-muted text-sm">
        {t('serviceContracts.documents.empty')}
      </p>
    );
  }

  const currentDocumentId = getCurrentDocumentId(documents);

  return (
    <>
      <ul className="divide-border border-border divide-y rounded-lg border">
        {documents.map((document) => (
          <li
            key={document.id}
            className="flex items-center justify-between gap-2 px-3 py-2"
          >
            <button
              type="button"
              onClick={() => handleOpen(document.id)}
              disabled={!document.dataAvailable}
              className="text-onsurface hover:text-primary disabled:hover:text-onsurface flex min-w-0 items-center gap-2 text-left text-sm disabled:opacity-50"
            >
              <FileText className="size-4 shrink-0" />
              <span className="truncate">{document.name}</span>
              {document.id === currentDocumentId && (
                <span className="border-border rounded-md bg-surface-darker text-onsurface shrink-0 border px-2 py-0.5 text-xs font-medium">
                  {t('serviceContracts.documents.currentLabel')}
                </span>
              )}
            </button>
            <button
              type="button"
              onClick={() => setPendingDeleteId(document.id)}
              className="text-muted hover:bg-surface-darker hover:text-error shrink-0 rounded-md p-1.5 focus:outline-none"
              aria-label={t('serviceContracts.documents.deleteLabel')}
            >
              <Trash2 className="size-4" />
            </button>
          </li>
        ))}
      </ul>

      <ConfirmModal
        isOpen={pendingDeleteId !== null}
        title={t('serviceContracts.documents.deleteTitle')}
        message={t('serviceContracts.documents.deleteMessage')}
        confirmLabel={t('common.delete')}
        cancelLabel={t('common.cancel')}
        onConfirm={handleConfirmDelete}
        onCancel={() => setPendingDeleteId(null)}
        isLoading={deleteDocument.isPending}
        variant="danger"
      />
    </>
  );
}

// Picks the newest document by creation date, falling back to the first row when dates are absent.
function getCurrentDocumentId(documents: ServiceContractDocument[]): string {
  const [firstDocument] = documents;
  if (!firstDocument) {
    return '';
  }

  const latestDocument = documents.reduce((latest, document) => {
    if (!document.createdAt) {
      return latest;
    }
    if (!latest.createdAt) {
      return document;
    }
    return new Date(document.createdAt) > new Date(latest.createdAt)
      ? document
      : latest;
  }, firstDocument);

  return latestDocument.id;
}
